import type { ThunkAction } from '../../types/app.types';
import { openModal } from './modal-module';
import { trackView, trackEvent } from '../../utils/analytics';

/*
    Types
*/
export type CodeRequestSuccess = {
  type: 'otp/CODE_REQUEST_SUCCESS';
  data: {
    phone: string;
    token: string;
    matchCode?: string;
  };
};
export type CodeRequestError = {
  type: 'otp/CODE_REQUEST_ERROR';
  data: {
    error: string;
  };
};
export type CodeVerificationSuccess = {
  type: 'otp/CODE_VERIFICATION_SUCCESS';
};
export type CodeVerificationError = {
  type: 'otp/CODE_VERIFICATION_ERROR';
  data: {
    error: string;
  };
};
export type OTPPasswordResetError = {
  type: 'otp/PASSWORD_RESET_ERROR';
  data: {
    error: string;
  };
};
export type OTPClearError = {
  type: 'otp/OTP_CLEAR_ERROR';
};
export type OTPAction =
  | CodeRequestSuccess
  | CodeRequestError
  | CodeVerificationSuccess
  | CodeVerificationError
  | OTPPasswordResetError
  | OTPClearError;
export type OTPState = {
  phone: string | null | undefined;
  token: string | null | undefined;
  matchCode?: string;
  error?: string;
};

/*
    Actions
*/
export function requestOTPCode(flow: string, mobileNumber: string, matchCode?: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    // Remove non-digit characters from phone number
    const formattedNumber = mobileNumber.replace(/\D/g, '');
    return blinkAPIClient
      .post(`/v2/user/account/otp/${flow}/send`, {
        mobileNumber: formattedNumber,
      })
      .then((res) => {
        const { result } = res.data;
        dispatch({
          type: 'otp/CODE_REQUEST_SUCCESS',
          data: {
            phone: formattedNumber,
            token: result.token,
            matchCode: matchCode ? matchCode : undefined,
          },
        });
        return result;
      })
      .catch((err) => {
        dispatch({
          type: 'otp/CODE_REQUEST_ERROR',
          data: {
            error: err.message,
          },
        });

        if (err && err.errorDetails && err.errorDetails.errorName) {
          dispatch(trackView(`OTP error ${err.errorDetails.errorName}`));
        }

        if (err && err.errorDetails && err.errorDetails.modal) {
          dispatch(openModal('otp-error'));
        }
      });
  };
}
export function verifyOTPCode(
  flow: string,
  token: string,
  otpCode: string,
  mobileNumber: string | null | undefined,
  matchCode?: string
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .post(`/v2/user/account/otp/${flow}/verify`, {
        otp_code: otpCode,
        tid: token,
        matchCode,
        mobileNumber: mobileNumber ? '+1' + mobileNumber : undefined,
      })
      .then((res) => {
        dispatch({
          type: 'otp/CODE_VERIFICATION_SUCCESS',
        });

        if (flow === 'register') {
          dispatch(
            trackEvent('Created Account', {
              type: 'user',
            })
          );
        }

        return res.data.result;
      })
      .catch((err) => {
        if (err && err.errorDetails && err.errorDetails.errorName) {
          dispatch(trackView(`OTP error ${err.errorDetails.errorName}`));
        }

        dispatch({
          type: 'otp/CODE_VERIFICATION_ERROR',
          data: {
            error: err.message,
          },
        });
      });
  };
}

/*
    Reducer
*/
const initState: OTPState = {
  token: null,
  phone: null,
};
export default function otpReducer(state: OTPState = { ...initState }, action: OTPAction): OTPState {
  switch (action.type) {
    case 'otp/CODE_REQUEST_SUCCESS': {
      return { ...state, ...action.data, error: undefined };
    }

    case 'otp/CODE_VERIFICATION_SUCCESS': {
      return { ...state, error: undefined };
    }

    case 'otp/CODE_REQUEST_ERROR': {
      return { ...state, ...action.data };
    }

    case 'otp/CODE_VERIFICATION_ERROR': {
      return { ...state, ...action.data };
    }

    case 'otp/PASSWORD_RESET_ERROR': {
      return { ...state, ...action.data };
    }

    case 'otp/OTP_CLEAR_ERROR': {
      return { ...state, error: undefined };
    }

    default:
      return state;
  }
}
